import { Box, Card, Flex, Grid, Text } from '@radix-ui/themes';
import { MetricHelper } from 'classes/helpers/metric.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { PitchDesignHelper } from 'classes/helpers/pitch-design.helper';
import { CommonCallout } from 'components/common/callouts';
import { DialogButton } from 'components/common/dialogs/button';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonInputLabel } from 'components/common/form/label';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonTextInput } from 'components/common/form/text';
import { GameDataPlayerSelector } from 'components/common/game-data-player-selector';
import { FlexTableWrapper } from 'components/common/layout/flex-table-wrapper';
import { StatsApiVsManualToggle } from 'components/common/stats-api-vs-manual-toggle';
import { CommonTableHoC } from 'components/common/table';
import usePitchListStore from 'components/sections/pitch-list/store/use-pitch-list-store';
import { MachineContext } from 'contexts/machine.context';
import { PITCH_TYPE_FILTER_OPTIONS } from 'enums/filters';
import { StatsApiManualFormMode } from 'enums/forms';
import { GDPlayerSelectorMode } from 'enums/game-data.enums';
import { AVG_METHOD } from 'enums/pitches';
import { t } from 'i18next';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { RowWithID } from 'interfaces/tables/main';
import { BallHelper } from 'lib_ts/classes/ball.helper';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import {
  BuildPriority,
  MAX_FREQUENCY,
  MIN_FREQUENCY,
  PitchType,
} from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import {
  IBallState,
  IPitch,
  ITrajectory,
  ITrajektRefBreak,
} from 'lib_ts/interfaces/pitches';
import { ISeamOrientation, ISpin } from 'lib_ts/interfaces/pitches/i-base';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';
import { clamp, first, isNil } from 'lodash';
import { ReactNode, useContext, useMemo, useState } from 'react';
import { useAuthStore } from 'stores/use-auth-store';

interface IProps {
  pitch: IPitch;
  shots: IMachineShot[];
  readOnly?: boolean;
  onPitchUpdated?: (pitch: IPitch) => void;
}

interface ITargetVsActualRow extends RowWithID {
  label: string;

  icon?: ReactNode;

  desired: string;
  actual: string;

  units?: string;
  sticky?: boolean;

  tooltip?: string;
}

const NA = '--';

const COLUMNS: ITypedTableColumn<ITargetVsActualRow>[] = [
  {
    label: 'common.pitch-metrics',
    key: 'label',
    formatFn: (m) => (
      <>
        {t(m.label)}
        &nbsp;
        {m.units && (
          <Text size={RADIX.TEXT.SIZE.SM} color={RADIX.COLOR.SECONDARY}>
            ({m.units})
          </Text>
        )}
        &nbsp;
        {m.icon}
      </>
    ),
  },
  {
    label: 'common.desired',
    key: 'desired',
    align: 'right',
    tooltipFn: (m) => m.tooltip,
  },
  {
    label: 'common.actual',
    key: 'actual',
    align: 'right',
    tooltipFn: (m: ITargetVsActualRow) => m.tooltip,
  },
];

export const PitchDetailsTab = (props: IProps) => {
  const { machine, unitFactors } = useContext(MachineContext);

  const current = useAuthStore((s) => s.current);
  const leagueTeamOptions = useAuthStore((s) => s.leagueTeamOptions);

  const [metadata, setMetadata] = useState<Partial<IPitch>>(props.pitch);
  const [saving, setSaving] = useState(false);

  const [activeTab, setActiveTab] = useState(() => {
    if (!current.mlb_stats_api) {
      // user does not have access to Stats API
      return StatsApiManualFormMode.Manual;
    }

    const { mlb_repertoire, pitcher, team } = metadata;

    // user has access to Stats API
    const isManual = !mlb_repertoire && (!!pitcher || !!team);

    if (isManual) {
      // i.e. existing data was input manually
      return StatsApiManualFormMode.Manual;
    }

    return StatsApiManualFormMode.StatsAPI;
  });

  const { bs: targetBs } = props.pitch;

  const data: ITargetVsActualRow[] = useMemo(() => {
    const validBss = props.shots
      .filter((s) => s.bs)
      .map((s) => s.bs as IBallState);

    const avgBs = AVG_METHOD.fn(validBss) as IBallState;

    const validTrajs = props.shots
      .filter((s) => s.traj)
      .map((s) => s.traj as ITrajectory);

    const avgTraj =
      validTrajs.length > 0
        ? (AVG_METHOD.fn(validTrajs) as ITrajectory)
        : undefined;

    const validBreaks = props.shots.map((s) => TrajHelper.getBreaksFromShot(s));

    const avgBreaks =
      validBreaks.length > 0
        ? (AVG_METHOD.fn(validBreaks) as ITrajektRefBreak)
        : undefined;

    const [d_speed, a_speed] = [props.pitch.traj, avgTraj].map((velo) => {
      if (!velo) {
        return undefined;
      }

      const speedMph = TrajHelper.getSpeedFromTraj(velo);
      return (unitFactors.fromSpeedMph * speedMph).toFixed(0);
    });

    const [d_spinMagnitude, a_spinMagnitude] = [targetBs, avgBs].map((bs) => {
      if (!bs) {
        return undefined;
      }

      if (!isNaN(bs.wnet)) {
        return bs.wnet.toFixed(0);
      }

      // fallback
      if (bs.wx !== undefined && bs.wy !== undefined && bs.wz !== undefined) {
        return BallHelper.getNetSpin(bs).toFixed(0);
      }
    });

    const [d_spin, a_spin] = [targetBs, avgBs].map((bs) => {
      if (bs) {
        return bs as Partial<ISpin>;
      }

      return undefined;
    });

    const d_seams = BallHelper.getPitchSeams(props.pitch);

    const validSeams = props.shots
      .map((s) => BallHelper.getShotSeams(s) as ISeamOrientation)
      .filter((s) => s);

    const a_seams =
      validSeams.length > 0
        ? BallHelper.getAverageSeams(props.pitch, validSeams)
        : undefined;

    const [d_pos_x, a_pos_x] = [props.pitch.traj, avgTraj].map((traj) => {
      if (!traj || isNil(traj.px)) {
        return undefined;
      }

      return (unitFactors.fromPosFt * traj.px).toFixed(1);
    });

    const [d_pos_y, a_pos_y] = [props.pitch.traj, avgTraj].map((traj) => {
      if (!traj || isNil(traj.py)) {
        return undefined;
      }

      // ? distance from mound in cm would be stupid
      return ((unitFactors.fromPosFt * traj.py) / 100).toFixed(1);
    });

    const [d_pos_z, a_pos_z] = [props.pitch.traj, avgTraj].map((traj) => {
      if (!traj || isNil(traj.pz)) {
        return undefined;
      }

      return (unitFactors.fromPosFt * traj.pz).toFixed(1);
    });

    const d_breaks =
      props.pitch.breaks ?? TrajHelper.getBreaks(props.pitch.traj);

    const a_breaks = avgBreaks;

    const output: ITargetVsActualRow[] = [
      {
        _id: 'speed',
        label: 'common.speed',
        units: machine.isMetric ? 'kph' : 'mph',
        desired: d_speed ?? NA,
        actual: a_speed ?? NA,
      },
      {
        _id: 'spin-rate',
        label: 'common.spin-rate',
        units: 'rpm',
        desired: d_spinMagnitude ?? NA,
        actual: a_spinMagnitude ?? NA,
      },
      {
        _id: 'spin-x',
        label: 'common.spin-x',
        units: 'rpm',
        desired:
          props.pitch.priority !== BuildPriority.Breaks
            ? (d_spin?.wx?.toFixed(0) ?? NA)
            : NA,
        actual: a_spin?.wx?.toFixed(0) ?? NA,
      },
      {
        _id: 'spin-y',
        label: 'common.spin-y',
        units: 'rpm',
        desired:
          props.pitch.priority !== BuildPriority.Breaks
            ? (d_spin?.wy?.toFixed(0) ?? NA)
            : NA,
        actual: a_spin?.wy?.toFixed(0) ?? NA,
      },
      {
        _id: 'spin-z',
        label: 'common.spin-z',
        units: 'rpm',
        desired:
          props.pitch.priority !== BuildPriority.Breaks
            ? (d_spin?.wz?.toFixed(0) ?? NA)
            : NA,
        actual: a_spin?.wz?.toFixed(0) ?? NA,
      },
      {
        _id: 'horizontal-break',
        label: 'common.horizontal-break',
        units: MetricHelper.getBreaksUnit(machine.isMetric),
        desired:
          d_breaks && props.pitch.priority === BuildPriority.Breaks
            ? (-1 * d_breaks.xInches * unitFactors.fromBreaksIn).toFixed(1)
            : NA,
        actual: a_breaks
          ? (-1 * a_breaks.xInches * unitFactors.fromBreaksIn).toFixed(1)
          : NA,
        tooltip: PitchDesignHelper.HB_TOOLTIP_TEXT,
      },
      {
        _id: 'vertical-break',
        label: 'common.vertical-break',
        units: MetricHelper.getBreaksUnit(machine.isMetric),
        desired:
          d_breaks && props.pitch.priority === BuildPriority.Breaks
            ? (d_breaks.zInches * unitFactors.fromBreaksIn).toFixed(1)
            : NA,
        actual: a_breaks
          ? (a_breaks.zInches * unitFactors.fromBreaksIn).toFixed(1)
          : NA,
      },
      {
        _id: 'seam-longitude',
        label: 'common.seam-longitude',
        units: 'deg',
        desired: d_seams ? d_seams.longitude_deg.toFixed(1) : NA,
        actual: a_seams ? a_seams.longitude_deg.toFixed(1) : NA,
      },
      {
        _id: 'seam-latitude',
        label: 'common.seam-latitude',
        units: 'deg',
        desired: d_seams ? d_seams.latitude_deg.toFixed(1) : NA,
        actual: a_seams ? a_seams.latitude_deg.toFixed(1) : NA,
      },
      {
        _id: 'x-position',
        label: 'common.x-position',
        units: MetricHelper.getPositionUnit(machine.isMetric),
        desired: d_pos_x ?? NA,
        actual: a_pos_x ?? NA,
      },
      {
        _id: 'y-position',
        label: 'common.y-position',
        units: MetricHelper.getLongPositionUnit(machine.isMetric),
        desired: d_pos_y ?? NA,
        actual: a_pos_y ?? NA,
      },
      {
        _id: 'z-position',
        label: 'common.z-position',
        units: MetricHelper.getPositionUnit(machine.isMetric),
        desired: d_pos_z ?? NA,
        actual: a_pos_z ?? NA,
      },
    ];

    return output;
  }, [targetBs, props.pitch, props.shots, machine.isMetric, unitFactors]);

  const footerRows = useMemo(() => {
    const output: ITargetVsActualRow[] = [
      {
        _id: 'number-of-samples',
        label: 'common.number-of-samples',
        desired: '',
        actual: props.shots.length.toFixed(0),
        sticky: true,
      },
    ];

    return output;
  }, [props.shots.length]);

  const onSave = async () => {
    if (!metadata.name || metadata.name.trim().length === 0) {
      NotifyHelper.error({
        message_md: 'Please provide a non-empty name for your pitch.',
      });
      return;
    }

    setSaving(true);

    try {
      const result = await usePitchListStore.getState().updatePitches({
        payloads: [
          {
            _id: props.pitch._id,
            name: metadata.name.trim(),
            year: metadata.year,
            frequency: metadata.frequency,
            type: metadata.type,
            pitcher: metadata.pitcher?.trim() || null,
            team: metadata.team?.trim() || null,
            customID: metadata.customID?.trim() || null,
            teamPk: metadata.teamPk,
            // reset mlb_repertoire if manually entering pitcher/team
            mlb_repertoire:
              activeTab === StatsApiManualFormMode.StatsAPI
                ? metadata.mlb_repertoire
                : null,
          },
        ],
      });

      const updatedPitch = first(result);

      if (updatedPitch) {
        setMetadata(updatedPitch);
        NotifyHelper.success({
          message_md: 'Pitch updated!',
        });

        props.onPitchUpdated?.(updatedPitch);
      }
    } catch (error) {
      console.error(error);
      NotifyHelper.error({
        message_md: 'An error occurred while saving your changes.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <ErrorBoundary identifier="PitchDetailsTab">
      <Flex
        gap={RADIX.FLEX.GAP.MD}
        direction="column"
        height="auto"
        flexGrow="1"
      >
        <Box>
          <Text size={RADIX.TEXT.SIZE.MD} weight="bold">
            {t('common.pitch-details')}
          </Text>
        </Box>
        <Grid
          columns="2"
          gap={RADIX.FLEX.GAP.LG}
          className={RADIX.VFLEX.WRAPPER}
        >
          <Flex gap={RADIX.FLEX.GAP.MD} direction="column">
            <CommonTextInput
              id="pitch-name"
              label="pl.pitch-name"
              name="name"
              value={metadata.name}
              disabled={props.readOnly}
              onChange={(v) =>
                setMetadata((prev) => ({
                  ...prev,
                  name: v,
                }))
              }
            />
            <CommonSearchInput
              id="type"
              name="type"
              label="common.pitch-type"
              options={PITCH_TYPE_FILTER_OPTIONS}
              values={metadata.type ? [metadata.type] : []}
              onChange={(v) =>
                setMetadata((prev) => ({
                  ...prev,
                  type: first(v) as PitchType,
                }))
              }
              disabled={props.readOnly}
              labelOptional
              optional
              skipSort
            />

            <CommonInputLabel label="common.pitcher" />

            <StatsApiVsManualToggle
              mode={activeTab}
              onChange={(v) => setActiveTab(v)}
            />

            {activeTab === StatsApiManualFormMode.StatsAPI && (
              <GameDataPlayerSelector
                mode={GDPlayerSelectorMode.Pitcher}
                model={metadata.mlb_repertoire}
                onChangeModel={(player) => {
                  if (player) {
                    setMetadata((prev) => ({
                      ...prev,
                      mlb_repertoire: {
                        playerPk: player.playerPk,
                        sportId: player.sportId,
                        season: player.season,
                      },
                      pitcher: player.name,
                      team: player._mx_team_details?.name ?? null,
                      teamPk: player._mx_team_details?.teamPk ?? null,
                    }));
                    return;
                  }

                  setMetadata((prev) => ({
                    ...prev,
                    mlb_repertoire: null,
                    pitcher: null,
                    team: null,
                    teamPk: null,
                  }));
                }}
                autoClose
              />
            )}

            {activeTab === StatsApiManualFormMode.Manual && (
              <Card className="no-background" size="1">
                <Flex gap={RADIX.FLEX.GAP.MD} direction="column">
                  <CommonTextInput
                    id="pitch-pitcher"
                    name="pitch.pitcher"
                    label="common.name"
                    placeholder="common.name"
                    value={metadata.pitcher ?? ''}
                    onChange={(v) =>
                      setMetadata((prev) => ({
                        ...prev,
                        pitcher: v,
                        mlb_repertoire: null, // changing details via manual entry should clear Stats API selection
                      }))
                    }
                  />

                  <CommonSearchInput
                    id="pitch-team"
                    name="pitch.team"
                    label="common.team"
                    values={metadata.team ? [metadata.team] : []}
                    options={leagueTeamOptions}
                    onChange={(v) =>
                      setMetadata((prev) => ({
                        ...prev,
                        team: first(v) || null,
                        mlb_repertoire: null, // changing details via manual entry should clear Stats API selection
                      }))
                    }
                    optional
                  />

                  <CommonTextInput
                    id="pitch-customID"
                    name="pitch.customID"
                    label="hitters.custom-id"
                    value={metadata.customID ?? ''}
                    placeholder={t('common.type-a-unique-value') as string}
                    onChange={(v) =>
                      setMetadata((prev) => ({
                        ...prev,
                        customID: v,
                        mlb_repertoire: null, // changing details via manual entry should clear Stats API selection
                      }))
                    }
                  />
                </Flex>
              </Card>
            )}

            <CommonTextInput
              id={`freq-${props.pitch._id}`}
              name={`freq-${props.pitch._id}`}
              value={metadata.frequency?.toString() ?? ''}
              label="common.frequency"
              disabled={props.readOnly}
              onNumericChange={(v) => {
                setMetadata((prev) => ({
                  ...prev,
                  frequency: clamp(v, MIN_FREQUENCY, MAX_FREQUENCY),
                }));
              }}
            />
          </Flex>

          <Flex direction="column" align="stretch">
            <FlexTableWrapper
              gap={RADIX.FLEX.GAP.MD}
              table={
                <CommonTableHoC
                  dataTestID="PitchDetailsTab"
                  identifier="PitchDetailsTabTable"
                  displayColumns={COLUMNS}
                  displayData={data}
                  footerRows={footerRows}
                  vFlex
                />
              }
              footer={
                !props.readOnly && props.shots.length === 0 ? (
                  <CommonCallout
                    text={t('pl.pitch-data-summary-no-shots-msg').toString()}
                  />
                ) : undefined
              }
            />
          </Flex>
        </Grid>
        {!props.readOnly && (
          <Flex gap={RADIX.FLEX.GAP.SM} justify="end">
            <DialogButton
              label="common.save"
              variant="solid"
              loading={saving}
              onClick={onSave}
            />
          </Flex>
        )}
      </Flex>
    </ErrorBoundary>
  );
};
